import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import QuoteForm from "../components/site/QuoteForm";
import FAQBlock from "../components/site/FAQBlock";
import useSEO from "../hooks/useSEO";
import {
  CalendarClock,
  ListChecks,
  ClipboardCheck,
  Wrench,
  Search,
  FileText,
  Cog,
  CheckCircle2,
} from "lucide-react";

const IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/lmp91ro0_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_52_44%20PM.png";

export default function Maintenance() {
  useSEO({
    title: "Fire Safety System Maintenance Ireland | Planned Preventative Maintenance",
    description:
      "Fire safety system maintenance — scheduled servicing, inspections, functional testing, fault diagnosis and reporting for businesses across Ireland.",
    canonicalPath: "/services/maintenance",
  });

  return (
    <>
      <PageHero
        eyebrow="Maintenance"
        title="Fire Safety System Maintenance"
        subtitle="Routine maintenance helps identify faults before they become costly problems and supports the ongoing reliability of fire safety systems."
        image={IMG}
        imageAlt="Engineer servicing fire safety equipment"
        ctaPrimary={{ to: "/contact", label: "Request a Maintenance Quote" }}
        ctaSecondary={{ to: "/contact", label: "Contact Our Team" }}
      />

      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="fss-overline">Our maintenance services</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Servicing built around your premises
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <div key={i} className="fss-card fss-card-hover" data-testid={`maint-card-${i}`}>
                <s.icon className="w-8 h-8 text-green-700" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="fss-overline">Planned Preventative Maintenance</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Structured, scheduled maintenance for your fire safety systems.
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>
                Fire Safety Systems can provide planned maintenance programmes
                tailored to the fire safety systems installed within each
                premises.
              </p>
              <p>
                These programmes should help businesses schedule routine
                inspections, testing and servicing while maintaining clear
                service records.
              </p>
            </div>
            <ul className="mt-6 space-y-3">
              {BENEFITS.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-8 rounded-sm">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-green-700 mb-4">
              Maintenance contract enquiry
            </div>
            <QuoteForm defaultService="Maintenance" />
          </div>
        </div>
      </section>

      <FAQBlock
        eyebrow="Planned maintenance FAQ"
        title="Frequently asked questions"
        items={[
          { q: "What is planned preventative maintenance?", a: "A scheduled programme of servicing and inspection designed to keep systems reliable, identify issues early and support clear ongoing records." },
          { q: "How often is servicing required?", a: "The right frequency depends on the systems installed and the premises. We will agree a suitable schedule as part of your maintenance plan." },
          { q: "Can FSS create a planned schedule?", a: "Yes — we build the schedule around the equipment installed, your operating hours and any multi-site coordination needs." },
          { q: "What records are provided?", a: "Following each visit we provide clear service information covering the equipment tested, faults identified and any recommended actions." },
          { q: "Can multiple sites be covered under one plan?", a: "Yes — we can coordinate site-by-site scheduling with central reporting for multi-site organisations." },
          { q: "What happens between planned visits?", a: "If a fault or issue arises between visits, contact us and we will advise on the appropriate next step." },
        ]}
        bg="slate"
      />

      <CTABanner
        title="Discuss a planned maintenance programme for your premises."
        subtitle="Talk to our team about a maintenance programme tailored to your fire safety systems."
        primary={{ to: "/contact", label: "Request a Maintenance Proposal" }}
        secondary={{ to: "/contact", label: "Contact Our Team" }}
      />
    </>
  );
}

const SERVICES = [
  { icon: CalendarClock, title: "Scheduled servicing", copy: "Routine servicing aligned to your fire safety programme." },
  { icon: ListChecks, title: "System inspections", copy: "On-site inspections carried out by experienced engineers." },
  { icon: ClipboardCheck, title: "Functional testing", copy: "Functional testing to help confirm systems are operating as intended." },
  { icon: Search, title: "Fault diagnosis", copy: "Investigation of intermittent or unclear system faults." },
  { icon: Wrench, title: "Repairs", copy: "Repair work carried out as part of ongoing servicing where required." },
  { icon: Cog, title: "Component replacement", copy: "Replacement of failed or degraded components." },
  { icon: FileText, title: "Detailed reporting", copy: "Clear service reports supporting your fire safety records." },
];

const BENEFITS = [
  "Identify faults before they become costly problems",
  "Support the ongoing reliability of fire safety systems",
  "Maintain clear, structured service records",
  "Reduce unplanned downtime through preventative servicing",
];
